/**
 * PresencePoller — polls /api/presence/bulk every 15s,
 * fires 'presence-update' CustomEvents on window when state changes.
 *
 * Usage:
 *   import { presencePoller } from './services/presence-poller';
 *   presencePoller.track(['1001','1002','1069']);
 *   presencePoller.start();
 *   // listen:
 *   window.addEventListener('presence-update', e => {
 *       const { extension, state } = e.detail;
 *   });
 */

import { api } from '../api';
import { updatePresence, updatePresenceBulk } from '../stores/presence';

const POLL_INTERVAL = 15_000; // 15 seconds
const MAX_BACKOFF = 120_000; // 2 minutes cap
const BACKOFF_BASE = 2_000; // initial retry delay

class PresencePoller {
    private _extensions: Set<string>;
    private _cache: Record<string, string>;
    private _callStates: Record<string, string>;
    private _timer: ReturnType<typeof setTimeout> | null;
    private _running: boolean;
    private _errorCount: number;

    constructor() {
        this._extensions = new Set();
        this._cache = {}; // extension -> last known presence state
        this._callStates = {}; // extension -> ringing|in_call|calling
        this._timer = null;
        this._running = false;
        this._errorCount = 0;
    }

    /** Add extensions to watch list. Accepts array or single string. */
    track(extensions: string | string[]): void {
        const list = Array.isArray(extensions) ? extensions : [extensions];
        list.forEach((ext) => {
            if (ext) this._extensions.add(String(ext));
        });
    }

    /** Remove extensions from watch list. */
    untrack(extensions: string | string[]): void {
        const list = Array.isArray(extensions) ? extensions : [extensions];
        list.forEach((ext) => this._extensions.delete(String(ext)));
    }

    /** Replace full tracking set. */
    setExtensions(extensions: string[]): void {
        this._extensions.clear();
        this.track(extensions);
    }

    /** Start polling. Safe to call multiple times. */
    start(): void {
        if (this._running) return;
        this._running = true;
        this._errorCount = 0;
        this._poll(); // immediate first poll
    }

    /** Stop polling and clear state. */
    stop(): void {
        this._running = false;
        if (this._timer) {
            clearTimeout(this._timer);
            this._timer = null;
        }
        this._cache = {};
        this._extensions.clear();
    }

    /** Get last known state for an extension (from cache). */
    getState(extension: string): string {
        return this._cache[extension] || 'offline';
    }

    /** Get call state: 'ringing', 'in_call', 'calling', or null. */
    getCallState(extension: string): string | null {
        return this._callStates[extension] || null;
    }

    /** Get full cache snapshot. */
    getAll(): Record<string, string> {
        return { ...this._cache };
    }

    /** Get all call states snapshot. */
    getAllCallStates(): Record<string, string> {
        return { ...this._callStates };
    }

    // ---- internals ----

    private async _poll(): Promise<void> {
        if (!this._running) return;

        if (this._extensions.size === 0) {
            this._schedule(POLL_INTERVAL);
            return;
        }

        try {
            const extList = Array.from(this._extensions).join(',');
            const data: Record<string, string> = await api.get(
                `/presence/bulk?extensions=${encodeURIComponent(extList)}`,
            );

            this._errorCount = 0; // reset backoff on success

            // Compare with cache, fire events for changes + sync to nanostore
            for (const [ext, state] of Object.entries(data)) {
                const prev = this._cache[ext];
                this._cache[ext] = state;
                if (prev !== state) {
                    updatePresence(ext, state);
                    window.dispatchEvent(
                        new CustomEvent('presence-update', {
                            detail: { extension: ext, state },
                        }),
                    );
                }
            }

            // Mark extensions not in response as offline
            for (const ext of this._extensions) {
                if (!(ext in data) && this._cache[ext] !== 'offline') {
                    this._cache[ext] = 'offline';
                    updatePresence(ext, 'offline');
                    window.dispatchEvent(
                        new CustomEvent('presence-update', {
                            detail: { extension: ext, state: 'offline' },
                        }),
                    );
                }
            }

            // Fetch call states (ringing/in_call) from Asterisk
            try {
                const callData: Record<string, string> = await api.get('/presence/call-states');
                const prevCalls = this._callStates;
                this._callStates = callData || {};

                // Fire events for call state changes
                const allExts = new Set([...Object.keys(prevCalls), ...Object.keys(this._callStates)]);
                for (const ext of allExts) {
                    const prev = prevCalls[ext] || null;
                    const curr = this._callStates[ext] || null;
                    if (prev !== curr) {
                        window.dispatchEvent(
                            new CustomEvent('call-state-update', {
                                detail: { extension: ext, callState: curr },
                            }),
                        );
                    }
                }
            } catch (e: unknown) {
                // Non-critical — just skip call states this round
            }

            // Sync full snapshot to nanostore + fire batch event
            updatePresenceBulk(this._cache);
            window.dispatchEvent(
                new CustomEvent('presence-bulk-update', {
                    detail: { ...this._cache },
                }),
            );

            // Poll faster when there are active calls
            const hasActiveCalls = Object.keys(this._callStates).length > 0;
            this._schedule(hasActiveCalls ? 5000 : POLL_INTERVAL);
        } catch (err: any) {
            this._errorCount++;
            const delay = Math.min(BACKOFF_BASE * Math.pow(2, this._errorCount - 1), MAX_BACKOFF);
            console.warn(`PresencePoller: error (attempt ${this._errorCount}), retry in ${delay}ms`, (err instanceof Error ? err.message : String(err)));
            this._schedule(delay);
        }
    }

    private _schedule(ms: number): void {
        if (!this._running) return;
        this._timer = setTimeout(() => this._poll(), ms);
    }
}

/** Singleton instance — shared across the app. */
export const presencePoller = new PresencePoller();
